import { AfterViewInit, Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import {
	AdjustmentCreditType,
	DefaultMatterAdjustmentOther,
	MatterAdjustmentOther,
	MatterAdjustmentOtherCalculated,
	OtherAdjustmentType,
	OtherAdjustmentVendor,
} from '@models/matter';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { AdjustmentDefaultDetailsSelectors } from '@state/global/adjustment-details';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import { RadioGroupItem } from 'src/app/shared/atomic/atoms/radio-group/radio-group.component';
import { calculateOther } from 'src/app/shared/services/adjustments/other';
import { ICON } from '@core/util/icon';
import { YES_NO_OPTIONS } from 'src/app/ui/utils/constants';

export interface OtherDialogData {
	adjustmentId?: string;
}

const OtherTypesOptions: RadioGroupItem<OtherAdjustmentType>[] = [
	{ value: OtherAdjustmentType.FIXED, label: 'Fixed' },
	{ value: OtherAdjustmentType.PRORATED, label: 'Prorated' },
];

const CreditOptions: RadioGroupItem<AdjustmentCreditType>[] = [
	{ value: AdjustmentCreditType.VENDOR, label: 'Vendor' },
	{ value: AdjustmentCreditType.PURCHASER, label: 'Purchaser' },
	{ value: AdjustmentCreditType.NONE, label: 'No adjustment' },
];

const VendorOptions: RadioGroupItem<OtherAdjustmentVendor>[] = [
	{ value: OtherAdjustmentVendor.PAYABLE, label: 'Payable' },
	{ value: OtherAdjustmentVendor.RECEIVABLE, label: 'Receivable' },
];

@Component({
	selector: 'app-other-dialog',
	templateUrl: './other-dialog.component.html',
	styleUrls: ['./other-dialog.component.scss'],
	animations: [openClose()],
	standalone: false,
})
export class OtherDialogComponent implements OnInit, AfterViewInit {
	public formSubmitted: boolean = false;
	public adjustment: MatterAdjustmentOther;
	public ICON = ICON;
	public otherTypesOptions = OtherTypesOptions;
	public otherTypes = OtherAdjustmentType;
	public creditOptions = CreditOptions;
	public yesNoOptions = YES_NO_OPTIONS;
	public vendorOptions = VendorOptions;
	public creditTypes = AdjustmentCreditType;
	public vendorTypes = OtherAdjustmentVendor;
	private id: string;
	public calculation: MatterAdjustmentOtherCalculated;
	public maxStartDate: string;
	public maxStartDateLabel: string;

	@ViewSelectSnapshot(AdjustmentsSelector.otherAdjustment)
	otherAdjustmentFilterFn: (id: string) => MatterAdjustmentOther;
	@SelectSnapshot(SelectedMatter.matterHST) hst: number;
	@SelectSnapshot(SelectedMatter.adjustmentDate) adjustmentDate: string;
	@SelectSnapshot(AdjustmentDefaultDetailsSelectors.adjustmentDetailsOther)
	public adjustmentDetails: boolean;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: OtherDialogData,
		private dialogRef: MatDialogRef<OtherDialogComponent>,
		private adjustmentDispatch: AdjustmentsDispatch,
	) {}

	ngOnInit(): void {
		this.id = this.data?.adjustmentId;
		this.adjustment = cloneDeep({
			...DefaultMatterAdjustmentOther,
			details: this.adjustmentDetails,
		});
		if (this.id) {
			this.adjustment = cloneDeep(this.otherAdjustmentFilterFn(this.id));
		}
	}

	ngAfterViewInit(): void {
		this.changed();
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	setAdjustmentCredit(value: AdjustmentCreditType) {
		if (value === AdjustmentCreditType.NONE) {
			this.adjustment.amount = 0;
		}
		this.changed();
	}

	changed() {
		this.calculation = calculateOther(
			this.adjustment,
			this.hst,
			this.adjustmentDate,
		);
		if (this.adjustmentDate && this.adjustment.endDate) {
			if (this.adjustmentDate > this.adjustment.endDate) {
				this.maxStartDate = this.adjustmentDate;
				this.maxStartDateLabel = 'Adjustment Date';
			} else {
				this.maxStartDate = this.adjustment.endDate;
				this.maxStartDateLabel = 'the End Date';
			}
		} else if (this.adjustmentDate) {
			this.maxStartDate = this.adjustmentDate;
			this.maxStartDateLabel = 'Adjustment Date';
		} else if (this.adjustment.endDate) {
			this.maxStartDate = this.adjustment.endDate;
			this.maxStartDateLabel = 'the End Date';
		}
	}

	saveModal() {
		this.formSubmitted = true;
		if (
			this.adjustment.otherType !== this.otherTypes.PRORATED ||
			(this.adjustment.startDate && this.adjustment.endDate)
		) {
			this.adjustmentDispatch.Set(this.adjustment);
			this.dialogRef.close();
		}
	}

	get vendorReceivedLabel() {
		return `Vendor has ${this.adjustment.vendor === OtherAdjustmentVendor.PAYABLE ? 'paid' : 'received'} full amount`;
	}

	get vendorShareLabel() {
		return `Vendor has ${this.adjustment.vendor === OtherAdjustmentVendor.PAYABLE ? 'paid' : 'received'}`;
	}
}
